import React from "react";
import { createSlug } from "../utilities";
import { normalizeRuns } from "../data";
import * as api from "../api/api";
import fileDownload from "js-file-download";
import { withErrorBoundary } from "../errors";
import { withTranslation } from "react-i18next";
import RunHistory from "../pages/RunHistory";

const createInitialState = () => ({
    loadingData: false,
    runs: undefined,

    selectedRun: undefined,
    log: undefined,

    deleting: false,

    notificationOpen: undefined,
    notificationVariant: undefined,
    notificationMessage: undefined,
});

/*
 * NOTE
 * Do not set the timer as state.  There were issues with attempting to clear the timer using useEffect.
 * Setting it on the outside appears to work as expected.
 * Example: Mount this component, then quickly unmount the component.
 * Before, the refreshTimer lingered on due to not being a dependancy of the useEffect.
 * But if it is a dependancy, then it will refire the useEffect, which triggers it's callback from the
 * previous time the useEffect was called, which cancels the signal.
 * Although the useEffect callback should only be fired on an unmount, this did not seem to be the case.
 */
let refreshTimer = undefined;
const refreshRate = 60000;

const getNewlyCompletedRunIds = (previousRuns, currentRuns) => {
    if (!previousRuns) {
        return [];
    }

    const previouslyCompletedRunIds = new Set((previousRuns || []).filter((run) => run.completed).map((run) => run.id));

    const currentlyCompletedRunIds = (currentRuns || []).filter((run) => run.completed).map((run) => run.id);

    return currentlyCompletedRunIds.filter((runId) => !previouslyCompletedRunIds.has(runId));
};

const RunHistoryContainer = (props) => {
    const [state, setState] = React.useState(createInitialState());

    const signal = api.getSignal();

    const setStateSafely = (newState) => {
        setState((prevState) => ({
            ...prevState,
            ...newState,
        }));
    };

    React.useEffect(() => {
        fetchRuns(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        return () => {
            if (refreshTimer) {
                window.clearTimeout(refreshTimer);
            }

            signal.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchRuns = (refreshing) => {
        setStateSafely({
            loadingData: !refreshing,
        });

        api.fetchRuns(true, true, signal.token)
            .then((response) => {
                if (!response) {
                    return;
                }

                const runs = Object.entries(normalizeRuns(response.data)).map(([id, run]) => ({
                    id: id,
                    name: run.name,
                    startedAt: run.startedAt,
                    completed: run.completed,
                    failed: run.failed,
                }));

                const newlyCompletedRunIds = getNewlyCompletedRunIds(state.runs, runs);

                const notification = newlyCompletedRunIds.length && {
                    notificationVariant: "success",
                    notificationOpen: true,
                    notificationMessage: props.t("{{runCount}} run(s) recently completed", {
                        runCount: newlyCompletedRunIds.length,
                    }),
                };

                setStateSafely({
                    runs: Object.entries(normalizeRuns(response.data)).map(([id, run]) => ({
                        id: id,
                        name: run.name,
                        startedAt: run.startedAt,
                        completed: run.completed,
                        failed: run.failed,
                    })),
                    ...(notification || {}),
                });

                refreshTimer = window.setTimeout(() => {
                    fetchRuns(true);
                }, refreshRate);
            })
            .finally(() => {
                setStateSafely({
                    loadingData: false,
                });
            });
    };

    const onManualRefresh = () => {
        if (refreshTimer) {
            window.clearTimeout(refreshTimer);
        }
        fetchRuns(true);
    };

    const onFetchLog = (runId) => {
        api.fetchRunLog(runId, signal.token).then((response) => {
            if (!response) {
                return;
            }

            const run = state.runs.find((run) => run.id === runId);

            if (run) {
                setStateSafely({
                    selectedRun: run,
                    log: response.data,
                });
            }
        });
    };

    const onCloseLog = () => {
        setStateSafely({
            selectedRun: undefined,
            log: undefined,
        });
    };

    const onDownloadRunLog = (runId) => {
        api.fetchRunLog(runId, signal.token).then((response) => {
            if (!response) {
                return;
            }

            const run = state.runs.find((run) => run.id === runId);

            if (run) {
                fileDownload(response.data, createSlug(run.name, "run.log"));
            }
        });
    };

    const onCloseNotification = () => {
        setStateSafely({
            notificationOpen: false,
        });
    };

    const onDeleteRun = (runId) => {
        setStateSafely({
            deleting: true,
        });

        api.deleteRuns([runId], signal.token)
            .then(() => {
                const runs = state.runs.filter((run) => run.id !== runId);

                setStateSafely({
                    runs: runs,
                    selectedRun: undefined,
                });
            })
            .finally(() => {
                setStateSafely({
                    deleting: false,
                });
            });
    };

    const onDownloadDatabases = (runId) => {
        api.downloadDatabases([runId], signal.token).then((response) => {
            if (!response) {
                return;
            }

            fileDownload(response.data, "databases.zip");
        });
    };

    return (
        <RunHistory
            loadingData={state.loadingData}
            runs={state.runs}
            notificationVariant={state.notificationVariant}
            notificationOpen={state.notificationOpen}
            notificationMessage={state.notificationMessage}
            onDownloadRunLog={onDownloadRunLog}
            onCloseNotification={onCloseNotification}
            onDeleteRun={onDeleteRun}
            onFetchLog={onFetchLog}
            onCloseLog={onCloseLog}
            log={state.log}
            onDownloadDatabases={onDownloadDatabases}
            deleting={state.deleting}
            onManualRefresh={onManualRefresh}
        />
    );
};

export default withTranslation()(withErrorBoundary(RunHistoryContainer));
